import MuiBox from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import { outcomeColors } from '../theme/chartPalette';

/**
 * The one number a view leads with.
 *
 * Deliberately constrained, because a hero figure only works if it is the only
 * thing shouting: exactly one per screen, and nothing else on the page competes
 * at this size.
 *
 * Three details that are specs rather than taste:
 *
 *   - The number is set in the same sans as everything else. A display or serif
 *     face on a figure reads as decoration bolted onto the product.
 *   - Proportional figures, not tabular. `tabular-nums` gives every digit the
 *     width of a zero, which makes a number like 121 look gappy at display
 *     sizes. Tabular belongs in columns that must line up, not here.
 *   - The number itself is set in ordinary ink, never in the status colour.
 *     Meaning travels on the labelled chip beside it — an icon and a word — so
 *     it survives for a reader who cannot separate green from red, and so the
 *     figure never has to clear a contrast bar a colour might fail.
 */
export function HeroFigure({ label, value, caption, status, statusLabel }) {
  const good = status === 'good';
  const StatusIcon = good ? TrendingUpIcon : TrendingDownIcon;
  const statusColor = good ? outcomeColors.good : outcomeColors.bad;

  // The root fills its card and centres itself in it. A hero figure usually
  // sits beside something taller — a chart explaining it — and left at its
  // natural height it strands a band of empty white underneath.
  return (
    <MuiBox
      sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', height: '100%', gap: 1 }}
    >
      {label ? (
        <Typography variant="body2" color="text.secondary">
          {label}
        </Typography>
      ) : null}

      {status ? (
        <MuiBox sx={{ display: 'inline-flex', alignItems: 'center', gap: 1, color: statusColor }}>
          {/* Icon plus word: the status is never carried by hue alone. */}
          <StatusIcon fontSize="small" aria-hidden="true" />
          <Typography variant="body2" sx={{ fontWeight: 600, color: 'inherit' }}>
            {statusLabel}
          </Typography>
        </MuiBox>
      ) : null}

      <Typography
        component="p"
        sx={{
          fontSize: { xs: '2.5rem', sm: '3rem' },
          fontWeight: 700,
          lineHeight: 1.1,
          letterSpacing: '-0.02em',
          fontVariantNumeric: 'proportional-nums',
          color: 'text.primary'
        }}
      >
        {value}
      </Typography>

      {caption ? (
        <Typography variant="body2" color="text.secondary">
          {caption}
        </Typography>
      ) : null}
    </MuiBox>
  );
}
